import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, currency, formatApiError } from "../lib/api";
import { openRazorpayCheckout } from "../lib/razorpay";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { Package, Send, User, TrendingUp, CreditCard, X, Truck, Boxes } from "lucide-react";
import { ImageUploader, FileUploader } from "../components/Uploader";

export default function Dashboard() {
  const { user, config } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("overview");
  const [profile, setProfile] = useState(null);
  const [enquiries, setEnquiries] = useState([]);
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [sub, setSub] = useState(null);
  const isPartner = user?.role === "PRINT_PARTNER";

  const reloadOrders = () => api.get("/seller/orders").then(r => setOrders(r.data)).catch(() => {});
  const reloadProducts = () => api.get("/me/products").then(r => setProducts(r.data)).catch(() => {});
  const reloadSub = () => api.get("/me/subscription").then(r => setSub(r.data)).catch(() => {});

  useEffect(() => {
    if (!user || user === false) return;
    (isPartner ? api.get("/me/partner") : api.get("/me/expert")).then(r => setProfile(r.data)).catch(() => {});
    api.get("/enquiries/received").then(r => setEnquiries(r.data)).catch(() => {});
    reloadProducts();
    reloadOrders();
    reloadSub();
  }, [user, isPartner]); // eslint-disable-line

  useEffect(() => {
    if (!user || user === false) return;
    if (user.role === "CUSTOMER") nav("/profile", { replace: true });
    else if (user.role === "ADMIN" || user.role === "SUPER_ADMIN") nav("/admin", { replace: true });
  }, [user, nav]);

  if (!user || user === false) return <div className="max-w-md mx-auto py-20 text-center"><Link to="/login" className="tl-btn-primary">Login</Link></div>;
  if (["CUSTOMER", "ADMIN", "SUPER_ADMIN"].includes(user.role)) return null;

  const tabs = [
    { id: "overview", label: "Overview", icon: TrendingUp },
    { id: "profile", label: "My Profile", icon: User },
    { id: "products", label: "Products & E-Files", icon: Package },
    { id: "orders", label: "Seller Orders", icon: Boxes },
    { id: "enquiries", label: "Enquiries", icon: Send },
    // Subscription tab is only exposed when the feature flag is on at launch time.
    ...(config?.subscription_enabled ? [{ id: "subscription", label: "Subscription", icon: CreditCard }] : []),
  ];

  return (
    <div data-testid="dashboard-page" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-6 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
        <div>
          <div className="tl-label mb-1">Dashboard</div>
          <h1 className="text-2xl sm:text-3xl font-display font-bold">Welcome, {user.name}</h1>
        </div>
        <div className={`text-xs px-3 py-1 rounded-full ${profile?.status === "LIVE" ? "bg-emerald-50 text-emerald-600 border border-emerald-500/30" : "bg-yellow-50 text-yellow-600 border border-yellow-500/30"}`}>
          {profile?.status || "NO APPLICATION"}
        </div>
      </div>

      <div className="flex overflow-x-auto no-scrollbar gap-1 mb-8 border-b border-slate-200">
        {tabs.map(t => (
          <button key={t.id} data-testid={`dash-tab-${t.id}`} onClick={() => setTab(t.id)}
            className={`shrink-0 flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${tab === t.id ? "border-[#0891B2] text-slate-900" : "border-transparent text-[#475569] hover:text-[#475569]"}`}>
            <t.icon className="w-4 h-4" />{t.label}
          </button>
        ))}
      </div>

      {tab === "overview" && (
        <div className="grid md:grid-cols-4 gap-4">
          <Stat label="Enquiries" value={enquiries.length} />
          <Stat label="Products/Files" value={products.length} />
          <Stat label="Seller Orders" value={orders.length} />
          <Stat label="Plan" value={sub?.plan_name || "None"} />
        </div>
      )}

      {tab === "profile" && (
        <div className="tl-card p-6">
          {profile ? (
            <div className="space-y-3">
              <div><div className="tl-label mb-1">Title</div><div>{profile.professional_title || profile.business_name}</div></div>
              <div><div className="tl-label mb-1">Location</div><div>{profile.location}</div></div>
              <div><div className="tl-label mb-1">About</div><div className="text-[#475569] text-sm">{profile.about || profile.description}</div></div>
              <Link to={isPartner ? "/join/partner" : "/join/expert"} className="tl-btn-primary mt-4 inline-flex">Edit Profile</Link>
            </div>
          ) : (
            <div className="text-center py-8">
              <p className="text-[#475569] mb-4">No profile yet. Complete your application to get started.</p>
              <Link to={isPartner ? "/join/partner" : "/join/expert"} className="tl-btn-primary inline-flex">Complete Application</Link>
            </div>
          )}
        </div>
      )}

      {tab === "products" && <ProductsPanel products={products} reload={reloadProducts} />}

      {tab === "orders" && <SellerOrdersPanel orders={orders} reload={reloadOrders} />}

      {tab === "enquiries" && (
        <div className="space-y-3">
          {enquiries.length === 0 ? <div className="tl-card p-8 text-center text-[#475569]">No enquiries yet.</div> : enquiries.map(e => (
            <div key={e.id} className="tl-card p-5">
              <div className="flex justify-between items-start mb-2">
                <div><div className="font-semibold">{e.project_title}</div><div className="text-xs text-[#475569]">from {e.customer_name} · {e.customer_email}</div></div>
                <span className="tl-chip">{e.status}</span>
              </div>
              <p className="text-sm text-[#475569] mb-3">{e.description}</p>
              {e.budget && <div className="text-xs text-[#475569] mb-3">Budget: {e.budget} · Deadline: {e.deadline || "flexible"}</div>}
              {e.conversation_id && <Link data-testid={`enq-open-${e.id}`} to={`/messages/${e.conversation_id}`} className="tl-btn-ghost text-xs">Open thread</Link>}
            </div>
          ))}
        </div>
      )}

      {tab === "subscription" && config?.subscription_enabled && <SubscriptionPanel sub={sub} onChange={reloadSub} />}
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="tl-card p-5 relative overflow-hidden">
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-[#2563EB]/60 via-[#0891B2]/40 to-transparent" />
      <div className="tl-label mb-2">{label}</div>
      <div className="font-display text-3xl font-bold tracking-tight">{value}</div>
    </div>
  );
}

function SellerOrdersPanel({ orders, reload }) {
  const [filter, setFilter] = useState("all");
  const filtered = orders.filter(o => filter === "all" ? true : filter === "physical" ? o.shipping_required : !o.shipping_required);

  const advance = async (o, next) => {
    try {
      await api.put(`/seller/orders/${o.id}/status`, { status: next });
      toast.success(`Marked ${next.replace(/_/g, " ").toLowerCase()}`);
      reload();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const schedulePickup = async (o) => {
    const date = window.prompt("Pickup date (YYYY-MM-DD)?", new Date(Date.now() + 86400000).toISOString().slice(0, 10));
    if (!date) return;
    try {
      await api.post(`/seller/orders/${o.id}/schedule-pickup`, { pickup_date: date });
      toast.success("Pickup scheduled (simulated — provider disabled)");
      reload();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div>
      <div className="flex gap-2 mb-4">
        {[["all","All"],["physical","Physical"],["digital","Digital"]].map(([k, l]) => (
          <button key={k} onClick={() => setFilter(k)}
            className={`px-3 py-1.5 rounded-full text-xs border ${filter === k ? "bg-[#2563EB] border-[#2563EB] text-slate-900" : "border-slate-200 text-[#475569]"}`}>{l}</button>
        ))}
      </div>
      {filtered.length === 0 ? (
        <div className="tl-card p-8 text-center text-[#475569]">No seller orders yet. Once customers buy your products, they'll appear here.</div>
      ) : (
        <div className="space-y-3">
          {filtered.map(o => (
            <div key={o.id} data-testid={`seller-order-${o.id}`} className="tl-card p-5">
              <div className="flex justify-between items-start mb-3">
                <div>
                  <div className="font-mono text-xs text-[#475569]">#{o.id.slice(0, 8)} · Parent {o.parent_order_id.slice(0, 8)}</div>
                  <div className="text-sm text-[#475569]">Buyer: {o.buyer_name} · {new Date(o.created_at).toLocaleString()}</div>
                </div>
                <div className="text-right">
                  <div className="font-display font-bold">{currency(o.subtotal)}</div>
                  <span className="tl-chip">{o.status}</span>
                </div>
              </div>
              <div className="space-y-1 mb-3">
                {o.items.map((it, i) => (
                  <div key={i} className="text-sm text-[#475569] flex justify-between">
                    <span>• {it.name} × {it.quantity}</span>
                    <span className="text-xs">{it.product_type}</span>
                  </div>
                ))}
              </div>
              {o.shipping_address && (
                <div data-testid={`so-address-${o.id}`} className="mb-3 text-xs text-[#475569] bg-[#FFFFFF] rounded p-3">
                  <div className="tl-label mb-1">Ship to</div>
                  <div className="text-[#334155]">{o.shipping_address.full_name} · {o.shipping_address.phone}</div>
                  <div>{[o.shipping_address.line1, o.shipping_address.line2].filter(Boolean).join(", ")}</div>
                  <div>{o.shipping_address.city}, {o.shipping_address.state} – {o.shipping_address.pincode}</div>
                </div>
              )}
              {o.shipping_required && (
                <div className="pt-3 border-t border-slate-200 flex flex-wrap gap-2">
                  {o.status === "PENDING_PROCESSING" && (
                    <button data-testid={`so-processing-${o.id}`} onClick={() => advance(o, "PROCESSING")} className="tl-btn-ghost text-xs">Start Processing</button>
                  )}
                  {o.status === "PROCESSING" && (
                    <button data-testid={`so-ready-${o.id}`} onClick={() => advance(o, "READY_TO_SHIP")} className="tl-btn-ghost text-xs">Mark Ready to Ship</button>
                  )}
                  {["READY_TO_SHIP", "PROCESSING"].includes(o.status) && (
                    <button data-testid={`so-pickup-${o.id}`} onClick={() => schedulePickup(o)} className="tl-btn-ghost text-xs inline-flex items-center gap-1"><Truck className="w-3.5 h-3.5" /> Schedule Pickup</button>
                  )}
                  {o.status === "READY_TO_SHIP" && (
                    <button data-testid={`so-shipped-${o.id}`} onClick={() => advance(o, "SHIPPED")} className="tl-btn-primary text-xs">Mark Shipped</button>
                  )}
                  {o.status === "SHIPPED" && (
                    <button data-testid={`so-delivered-${o.id}`} onClick={() => advance(o, "DELIVERED")} className="tl-btn-primary text-xs">Mark Delivered</button>
                  )}
                  {o.shipping?.note && <span className="text-xs text-yellow-600/80">⚠ {o.shipping.note}</span>}
                </div>
              )}
              {!o.shipping_required && (
                <div className="pt-3 border-t border-slate-200">
                  <span className="text-xs text-emerald-600">✓ Digital delivery complete — entitlement granted to buyer</span>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ProductsPanel({ products, reload }) {
  const [show, setShow] = useState(false);
  const [form, setForm] = useState({ name: "", description: "", category: "e-file", price: 0, images: [], tags: "", is_free: false, stock: 0, files: [] });
  const submit = async (e) => {
    e.preventDefault();
    try {
      const isEfile = form.category === "e-file";
      await api.post("/me/products", {
        ...form,
        price: Number(form.price) || 0,
        stock: Number(form.stock) || 0,
        images: form.images,
        file_url: isEfile ? (form.files[0]?.url || "") : undefined,
        file_id: isEfile ? (form.files[0]?.id || undefined) : undefined,
        tags: form.tags.split(",").map(s => s.trim()).filter(Boolean),
      });
      toast.success("Submitted for admin review");
      setShow(false); reload();
      setForm({ name: "", description: "", category: "e-file", price: 0, images: [], tags: "", is_free: false, stock: 0, files: [] });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <div>
      <div className="flex justify-between mb-4">
        <h3 className="font-display font-semibold">Your Products / E-Files</h3>
        <button data-testid="add-product-btn" onClick={() => setShow(true)} className="tl-btn-primary text-sm">Add New</button>
      </div>
      {products.length === 0 ? <div className="tl-card p-8 text-center text-[#475569]">Nothing yet. Click "Add New" to publish an E-File or product.</div> : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {products.map(p => (
            <div key={p.id} className="tl-card p-4">
              <img src={(p.images || [])[0] || "https://images.unsplash.com/photo-1638959492386-f9a68d55c374?crop=entropy&cs=srgb&fm=jpg&q=85&w=400"}
                onError={(e) => { e.currentTarget.src = "https://images.unsplash.com/photo-1638959492386-f9a68d55c374?crop=entropy&cs=srgb&fm=jpg&q=85&w=400"; }}
                alt={p.name} className="aspect-square object-cover rounded mb-3" />
              <div className="font-semibold text-sm line-clamp-1">{p.name}</div>
              <div className="flex justify-between items-center mt-1">
                <span className="text-xs text-[#475569]">{p.product_type === "digital" ? "Digital" : "Physical"} · {p.category}</span>
                <span className="tl-chip text-[10px]">{p.status}</span>
              </div>
              <div className="text-sm mt-1">{p.is_free ? "FREE" : currency(p.price)}{p.shipping_required && p.stock !== undefined && <span className="text-xs text-[#475569]"> · Stock {p.stock}</span>}</div>
            </div>
          ))}
        </div>
      )}
      {show && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4">
          <div className="bg-[#F8FAFC] border border-slate-200 rounded-xl max-w-lg w-full p-6 max-h-[92vh] overflow-y-auto">
            <div className="flex justify-between mb-4">
              <h3 className="font-display text-xl font-semibold">Add Product / E-File</h3>
              <button onClick={() => setShow(false)}><X className="w-5 h-5" /></button>
            </div>
            <form onSubmit={submit} className="flex flex-col gap-3">
              <input data-testid="new-prod-name" required placeholder="Product name" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="tl-input" />
              <textarea required rows={3} placeholder="Description" value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} className="tl-input" />
              <select value={form.category} onChange={e => setForm({ ...form, category: e.target.value })} className="tl-input">
                <option value="e-file">E-File (Digital, no shipping)</option>
                <option value="printed-product">3D Printed Product (Physical)</option>
              </select>
              <div className="grid grid-cols-2 gap-3">
                <input type="number" placeholder="Price ₹" value={form.price} onChange={e => setForm({ ...form, price: e.target.value })} className="tl-input" />
                {form.category !== "e-file" && (
                  <input type="number" placeholder="Stock" value={form.stock} onChange={e => setForm({ ...form, stock: e.target.value })} className="tl-input" />
                )}
              </div>
              <div>
                <div className="tl-label mb-2">Product images</div>
                <ImageUploader value={form.images} onChange={v => setForm({ ...form, images: v })} maxFiles={6} testId="new-prod-images" />
              </div>
              {form.category === "e-file" && (
                <div>
                  <div className="tl-label mb-2">3D file (STL, OBJ, STEP)</div>
                  <FileUploader value={form.files} onChange={v => setForm({ ...form, files: v })} testId="new-prod-file" />
                </div>
              )}
              <input placeholder="Tags (comma separated)" value={form.tags} onChange={e => setForm({ ...form, tags: e.target.value })} className="tl-input" />
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.is_free} onChange={e => setForm({ ...form, is_free: e.target.checked })} /> Free download</label>
              <button data-testid="new-prod-submit" className="tl-btn-primary justify-center">Submit for Review</button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

function SubscriptionPanel({ sub, onChange }) {
  const { user } = useAuth();
  const [plans, setPlans] = useState([]);
  useEffect(() => { api.get(`/subscription-plans?t=${Date.now()}`).then(r => setPlans(r.data)); }, []);
  const subscribe = async (id) => {
    try {
      const { data } = await api.post("/subscribe", { plan_id: id });
      if (data.requires_payment) {
        try {
          await openRazorpayCheckout({ ...data, kind: "subscription" }, user);
          toast.success("Subscription activated");
          onChange();
        } catch (err) { toast.error(err.message || "Payment cancelled"); }
      } else {
        toast.success("Subscription updated");
        onChange();
      }
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <div>
      {sub && <div className="tl-card p-5 mb-4"><div className="tl-label mb-2">Current Plan</div><div className="font-display text-2xl font-bold">{sub.plan_name}</div><div className="text-sm text-[#475569]">Renews {sub.renews_at ? new Date(sub.renews_at).toLocaleDateString() : "—"}</div><div className="mt-2 text-xs"><span className="tl-chip">{sub.status}</span></div></div>}
      <div className="grid md:grid-cols-3 gap-4">
        {plans.map(p => {
          const isCurrent = sub?.plan_id === p.id && sub?.status === "ACTIVE";
          const isFree = p.price === 0;
          const hasOffer = p.mrp && p.mrp > p.price;
          return (
            <div key={p.id} data-testid={`plan-${p.id}`} className={`tl-card p-6 relative ${sub?.plan_id === p.id ? "border-[#0891B2]" : ""}`}>
              <div className="flex items-center gap-2 mb-1">
                <div className="font-display text-xl font-bold">{p.name}</div>
                {p.badge && <span data-testid={`plan-${p.id}-badge`} className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#0891B2]/15 border border-[#0891B2]/30 text-[#0891B2] font-medium">{p.badge}</span>}
              </div>
              {isFree ? (
                <div className="font-display text-3xl font-bold mb-4">Free<span className="text-sm text-[#475569] font-normal">/mo</span></div>
              ) : (
                <div className="mb-4">
                  {hasOffer && (
                    <div data-testid={`plan-${p.id}-mrp`} className="text-sm text-[#64748B] line-through leading-tight">
                      {currency(p.mrp)}/month
                    </div>
                  )}
                  <div data-testid={`plan-${p.id}-price`} className="font-display text-3xl font-bold leading-tight">
                    {currency(p.price)}<span className="text-sm text-[#475569] font-normal">/month</span>
                  </div>
                </div>
              )}
              <ul className="space-y-2 mb-6">{p.features.map(f => <li key={f} className="text-sm text-[#475569]">• {f}</li>)}</ul>
              <button data-testid={`subscribe-${p.id}`} onClick={() => subscribe(p.id)} className={isCurrent ? "tl-btn-ghost w-full justify-center" : "tl-btn-primary w-full justify-center"}>
                {isCurrent ? "Current Plan" : isFree ? "Choose Plan" : `Get ${p.name} – ${currency(p.price)}/month`}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
